import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  ViewStyle,
  Dimensions,
} from 'react-native';
import { PricingPlan } from '../../shared/types';
import {
  formatPricingPlan,
  formatTrialPeriod,
} from '../../shared/utils/formatting';
import { CheckoutButton } from './CheckoutButton';

const { width: screenWidth } = Dimensions.get('window');

interface PricingTableProps {
  plans: PricingPlan[];
  customerId?: string;
  customerEmail?: string;
  onCheckoutSuccess?: (plan: PricingPlan) => void;
  onCheckoutError?: (plan: PricingPlan, error: string) => void;
  showFeatures?: boolean;
  showTrialInfo?: boolean;
  layout?: 'carousel' | 'list';
  containerStyle?: ViewStyle;
}

export const PricingTable: React.FC<PricingTableProps> = ({
  plans,
  customerId,
  customerEmail,
  onCheckoutSuccess,
  onCheckoutError,
  showFeatures = true,
  showTrialInfo = true,
  layout = 'carousel',
  containerStyle,
}) => {
  if (!plans || plans.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No pricing plans available</Text>
      </View>
    );
  }

  const renderPricingCard = (plan: PricingPlan) => (
    <PricingCard
      key={plan.id}
      plan={plan}
      customerId={customerId}
      customerEmail={customerEmail}
      onCheckoutSuccess={() => onCheckoutSuccess?.(plan)}
      onCheckoutError={error => onCheckoutError?.(plan, error)}
      showFeatures={showFeatures}
      showTrialInfo={showTrialInfo}
      style={layout === 'carousel' ? styles.carouselCard : styles.listCard}
    />
  );

  if (layout === 'carousel') {
    return (
      <View style={[styles.container, containerStyle]}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.carouselContainer}
          snapToInterval={screenWidth * 0.8 + 16}
          decelerationRate="fast"
        >
          {plans.map(renderPricingCard)}
        </ScrollView>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, containerStyle]}>
      <View style={styles.listContainer}>{plans.map(renderPricingCard)}</View>
    </ScrollView>
  );
};

interface PricingCardProps {
  plan: PricingPlan;
  customerId?: string;
  customerEmail?: string;
  onCheckoutSuccess?: () => void;
  onCheckoutError?: (error: string) => void;
  showFeatures?: boolean;
  showTrialInfo?: boolean;
  style?: ViewStyle;
}

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  customerId,
  customerEmail,
  onCheckoutSuccess,
  onCheckoutError,
  showFeatures = true,
  showTrialInfo = true,
  style,
}) => {
  const isPopular = plan.popular;

  return (
    <View style={[styles.card, isPopular && styles.popularCard, style]}>
      {isPopular && (
        <View style={styles.popularBadge}>
          <Text style={styles.popularBadgeText}>Most Popular</Text>
        </View>
      )}

      {/* Plan Header */}
      <View style={styles.cardHeader}>
        <Text style={styles.planName}>{plan.name}</Text>
        {plan.description && (
          <Text style={styles.planDescription}>{plan.description}</Text>
        )}
        <Text style={styles.planPrice}>{formatPricingPlan(plan)}</Text>
        {showTrialInfo && plan.trialPeriodDays && plan.trialPeriodDays > 0 && (
          <Text style={styles.trialInfo}>
            {formatTrialPeriod(plan.trialPeriodDays)}
          </Text>
        )}
      </View>

      {/* Features List */}
      {showFeatures && plan.features && plan.features.length > 0 && (
        <View style={styles.featuresContainer}>
          {plan.features.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <Text style={styles.featureCheckmark}>✓</Text>
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>
      )}

      {/* CTA Button */}
      <View style={styles.buttonContainer}>
        <CheckoutButton
          priceId={plan.stripePriceId}
          customerId={customerId}
          customerEmail={customerEmail}
          trialPeriodDays={plan.trialPeriodDays}
          metadata={plan.metadata}
          onSuccess={onCheckoutSuccess}
          onError={onCheckoutError}
          style={[
            styles.ctaButton,
            isPopular ? styles.popularButton : styles.regularButton,
          ]}
          textStyle={[
            styles.ctaButtonText,
            isPopular ? styles.popularButtonText : styles.regularButtonText,
          ]}
        >
          Get Started
        </CheckoutButton>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  carouselContainer: {
    paddingHorizontal: 16,
  },
  listContainer: {
    padding: 16,
  },
  carouselCard: {
    width: screenWidth * 0.8,
    marginRight: 16,
  },
  listCard: {
    marginBottom: 16,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  popularCard: {
    borderColor: '#007AFF',
    borderWidth: 2,
  },
  popularBadge: {
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1,
    backgroundColor: '#007AFF',
    paddingVertical: 8,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    alignItems: 'center',
  },
  popularBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  cardHeader: {
    alignItems: 'center',
    marginBottom: 20,
    marginTop: 20, // Extra space for popular badge
  },
  planName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  planDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  planPrice: {
    fontSize: 28,
    fontWeight: '800',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  trialInfo: {
    fontSize: 12,
    color: '#22c55e',
    fontWeight: '600',
    textAlign: 'center',
  },
  featuresContainer: {
    marginBottom: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  featureCheckmark: {
    color: '#22c55e',
    fontSize: 16,
    fontWeight: '700',
    marginRight: 12,
    marginTop: 2,
  },
  featureText: {
    flex: 1,
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
  },
  buttonContainer: {
    marginTop: 'auto',
  },
  ctaButton: {
    paddingVertical: 16,
    borderRadius: 8,
  },
  ctaButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  popularButton: {
    backgroundColor: '#007AFF',
  },
  popularButtonText: {
    color: '#ffffff',
  },
  regularButton: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  regularButtonText: {
    color: '#333',
  },
});

export default PricingTable;
